/**
 * 快速放大模块 (module=20)
 *
 * 使用 Poller 轮询通信，保持原有逻辑不变。
 *
 * @see research.md 第1节 - WebSocket 通信模式
 */

import { ModuleEnum } from '../moduleEnum';
import { ModuleId } from '@xmiles/holopix-types';
import type { LayoutReferState } from '@/types/image';
import { useEventTracking } from '@/hooks/useEventTracking';
import { useGeneratingVersions } from '@/hooks/useGeneratingVersions';
import { getClientId, initGenerateResult } from '@/hooks/useGenerate';
import type { ModuleRoute, Module } from '@/types/artwork/generate';
import { request } from '@/axios';
import { Poller, defaultCheckCompletion } from '@/utils/QuickTaskPoller';
import { $bus } from '@/mitt';
import { useProgress } from '@/hooks/useProgress';
import { registerZoomModule, type ZoomModule } from './zoomRegistry';
import type { FastZoomParams, ZoomStyle, ZoomMode, UpscaleSize, DetailIntensity } from '@/types/artwork/zoom';

// ============================================================
// 模块 ID 和常量
// ============================================================

/** 快速放大模块 ID */
export const ZOOM_FAST_MODULE_ID = ModuleId.Zoom; // 20

export const module = ModuleEnum['zoom'];

// ============================================================
// 双模式参数结构
// ============================================================

/** 快速放大参数（状态存储用） */
const fastParamsState = {
  rescaleFactor: 2,
  superImgType: 'person' as ZoomStyle,
};

/** 重绘放大参数（状态存储用） */
const redrawParamsState = {
  upscaleSize: '4k' as UpscaleSize,
  detailIntensity: 3 as DetailIntensity,
  superImgType: 'person' as ZoomStyle,
};

/**
 * Zoom 模块参数（支持双模式独立存储）
 * @see data-model.md 第 2.2 节 - 默认参数值
 */
export const zoomParams = {
  /** 当前选中的放大模式 */
  mode: 'redraw' as ZoomMode,

  /** 原始稿件引用 */
  layoutRefer: null as null | LayoutReferState,

  /** 原始稿件 ID（旧面板兼容字段） */
  layoutReferId: undefined as number | undefined,

  /** 快速放大参数 */
  fast: { ...fastParamsState },

  /** 重绘放大参数 */
  redraw: { ...redrawParamsState },

  // ============================================================
  // 旧面板兼容字段（保留以支持 ZoomPanelLegacy.vue）
  // ============================================================
  module,
  rescaleFactor: 2,
  superImgType: 'person' as ZoomStyle,
};

// ============================================================
// 算力计算
// ============================================================

import { useArtworkStore } from '@/store/artwork';
import { useUserStore } from '@/store/user';
import { filteringInvalidFields } from '@/utils';
import { getModuleLabelById, getConsumeTime as helperGetConsumeTime } from '@xmiles/holopix-helper';
import { getZoomConsumePower } from '@xmiles/holopix-helper';

/**
 * 获取快速放大算力消耗
 * @param params 参数
 * @returns 算力消耗点数
 */
export function getConsumePower(params: Partial<FastZoomParams> = {}): number {
  const artworkStore = useArtworkStore();
  const layoutRefer = params.layoutRefer || artworkStore.zoom.params.layoutRefer;

  return getZoomConsumePower({
    zoomMode: 'fast',
    rescaleFactor: params.rescaleFactor || 2,
    superImgType: params.superImgType || 'person',
    layoutRefer: layoutRefer as any,
  });
}

/**
 * 获取快速放大预计时间
 * @param params 参数
 * @returns 预计时间（秒）
 */
export function getConsumeTime(params: Partial<FastZoomParams> = {}): number {
  // 快速放大预计 10 秒
  return helperGetConsumeTime(module, params);
}

// ============================================================
// 生成函数
// ============================================================

function getZoomGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  const params = {
    module,
    source: 1,
    userId: userStore.userInfo.id,
    artworkId: artworkStore.artworkState.id,
    layoutRefer: artworkStore.zoom.params.layoutRefer,
    layoutReferId: artworkStore.zoom.params.layoutRefer?.id,
    originImgUrl: artworkStore.zoom.params.layoutRefer?.image,
    rescaleFactor: artworkStore.zoom.params.fast?.rescaleFactor || 2,
    superImgType: artworkStore.zoom.params.fast?.superImgType || 'person',
    batchSize: 1,
  } as any;

  // 去除 layoutRefer 中的无效字段
  filteringInvalidFields(params.layoutRefer, ['id', 'width', 'height', 'image']);

  return params;
}

// 查询生图状态
function queryGenerateStatus(data: {
  module: Module;
  apiClientId: string;
  userId: string;
  artworkId: number;
  originImgUrl: string;
  rescaleFactor: string;
  superImgType: 'cartoon' | 'person';
}) {
  return request({
    api: '/aiApi/superImg',
    method: 'post',
    data: { message: JSON.stringify(data) },
    notToast: false,
  });
}

// 定义响应接口
interface MattingResponse {
  msg: string;
  code: number;
  data: {
    imgUrl: string | null;
    imgId: string | null;
    status: number;
    requestId: string;
    msg: string | null;
    width?: number;
    height?: number;
  };
  success: boolean;
}

const { start, close, onProgressUpdated } = useProgress();

/**
 * 启动快速放大生成
 * @param oldParams 可选的旧参数（用于重试/编辑场景）
 */
export async function startZoomGenerate(oldParams?: any) {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  const params = oldParams ?? getZoomGenerateParam();
  const clientId = oldParams?.apiClientId || getClientId();
  params.apiClientId = clientId;

  const poller = new Poller<MattingResponse>({
    interval: 2000,
    shouldLog: true,
    logMessage: `耗时计算-快速放大`,
  });

  const { set, updateResult, isModuleLatest, del } = useGeneratingVersions();

  poller
    .on('complete', async (result: any) => {
      // 通知历史记录侧边栏有新的版本生成
      $bus.emit('hasNewVersion');

      // 该模块最新一次生图
      if (isModuleLatest(clientId)) {
        artworkStore.resetModuleLastVersionParams(module, params);

        // 更新生图面板状态（直接使用 'zoom' 避免类型问题）
        artworkStore.zoom.result = {
          ...artworkStore.zoom.result,
          code: 3,
          data: {
            status: 3,
            images: [
              {
                id: result.data.imgId,
                width: result.data.width,
                height: result.data.height,
                image: result.data.imgUrl,
              },
            ] as any,
            param: JSON.stringify(params),
          },
        };

        // 移除该条生图记录
        del(clientId);

        close();
      }

      reportFinishEventTracking({
        result: 1,
        imageIds: result.data.imgUrl,
      });
    })
    .on('error', (error: any) => {
      console.error('快速放大错误:', error);

      del(clientId);
      artworkStore.setModuleLatest(module);

      // 关闭虚拟进度条
      close();

      // 生图失败、更新用户算力
      userStore.queryUserRights();

      reportFinishEventTracking({
        result: 0,
      });
    })
    .poll(() => queryGenerateStatus(params), defaultCheckCompletion);

  close();

  const estimatedTime = getConsumeTime(params);
  start(estimatedTime);

  // 更新生图算力
  setTimeout(() => {
    userStore.queryUserRights();
  }, 1500);

  onProgressUpdated((progress) => {
    updateResult(clientId, { progress });
  });

  // 添加生图记录
  set({
    module,
    clientId,
    params,
    result: { ...initGenerateResult, code: 2, progress: 1, msg: '正在快速生成中' },
    socket: undefined,
    cancel: poller.cancel,
  });

  // 更新生图面板状态（直接使用 'zoom' 避免类型问题）
  artworkStore.zoom.result = {
    ...artworkStore.zoom.result,
    code: 2,
  };

  // 提交生图埋点
  function reportStartEventTracking() {
    const eventValue: any = {
      client_id: clientId,
      source: '直接生成',
      module: getModuleLabelById(module) as string,
      artwork_id: artworkStore.artworkState.id.toString(),
      batchSize: params.batchSize,
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
      zoom_mode: params.superImgType === 'person' ? '写实' : '动漫',
    };

    useEventTracking({
      event: 'creation_event',
      eventValue,
    });
  }
  reportStartEventTracking();

  function reportFinishEventTracking({
    result = 0 as 0 | 1,
    imageIds = '',
    queue_time_number = 0,
    loading_time_number = 0,
  } = {}) {
    useEventTracking({
      event: 'artwork_status_event',
      eventValue: {
        module: getModuleLabelById(module) as string,
        client_id: clientId,
        source: '直接生成',
        img_id: imageIds,
        result,
        queue_time_number,
        loading_time_number,
        is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
        zoom_mode: params.superImgType === 'person' ? '写实' : '动漫',
      },
    });
  }
}

// ============================================================
// 导出模块（供注册表使用）
// ============================================================

export const zoomFastModule: ZoomModule = {
  startGenerate: startZoomGenerate as any,
  getConsumePower,
  getConsumeTime,
};

// 自动注册到注册表
registerZoomModule('fast', () => Promise.resolve(zoomFastModule));
